import React, { useEffect, useMemo, useState } from "react";
import {
  FaUniversity,
  FaUserGraduate,
  FaSearch,
  FaExclamationCircle,
  FaLayerGroup,
  FaTimes,
  FaUsers,
  FaCheckCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";
import SummaryApi from "../common";
import { getSessionEmployeeId } from "../utils/sessionUser";
const AVATAR_GRADIENTS = [
  "linear-gradient(135deg, #e05260, #c1272d)",
  "linear-gradient(135deg, #6366f1, #4338ca)",
  "linear-gradient(135deg, #10b981, #047857)",
  "linear-gradient(135deg, #f59e0b, #d97706)",
  "linear-gradient(135deg, #06b6d4, #0e7490)",
  "linear-gradient(135deg, #8b5cf6, #6d28d9)",
];

const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "NA";

const getAvatarGradient = (seed = "") => {
  let hash = 0;

  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }

  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
};

const AssignUniversity = () => {
  // ============================================================
  // EMPLOYEES
  // ============================================================

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  // ============================================================
  // UNIVERSITIES
  // ============================================================

  const [universities, setUniversities] = useState([]);
  const [universityLoading, setUniversityLoading] = useState(true);

  // ============================================================
  // SEARCH / FILTER
  // ============================================================

  const [searchTerm, setSearchTerm] = useState("");

  // "all" | "unassigned" | university value
  const [selectedGroup, setSelectedGroup] = useState("all");

  // ============================================================
  // ASSIGNMENT DATA
  // employee_id -> university ids
  // ============================================================

  const [assignments, setAssignments] = useState({});
  const [assigning, setAssigning] = useState(false);
  // ============================================================
  // EMPLOYEE SELECTION
  // ============================================================

  const [selectedEmployees, setSelectedEmployees] = useState([]);
  // TODO: replace with however you identify the logged-in admin
  const getAssignedBy = () => localStorage.getItem("user_id") || null;
  // ============================================================
  // UNIVERSITY SELECTION (single select)
  // ============================================================

  const [pendingUniversity, setPendingUniversity] = useState(null);

  const headers = {
    Authorization: "Bearer sandeep_university_916",
    "Content-Type": "application/json",
  };

  // ============================================================
  // FETCH EMPLOYEES
  // ============================================================

  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);

      try {
        const response = await fetch(SummaryApi.getAllEmployees.url, {
          method: SummaryApi.getAllEmployees.method,
          headers,
        });

        if (!response.ok) {
          throw new Error("Failed to fetch employees");
        }

        const result = await response.json();

        if (result?.status && Array.isArray(result.data)) {
          setEmployees(result.data);
        } else {
          setEmployees([]);
        }
      } catch (error) {
        console.error("Employee API Error:", error);

        setEmployees([]);

        toast.error(`Error fetching employees: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);



  // ============================================================
  // COUNTS
  // ============================================================
  const isAssignedToPending = (employeeId) =>
    pendingUniversity !== null &&
    (assignments[employeeId] || []).some(
      (value) => String(value) === String(pendingUniversity),
    );

  // Only these are actually sent to the backend
  const newSelectedEmployees = selectedEmployees.filter(
    (id) => !isAssignedToPending(id),
  );

  const alreadyAssignedCount = employees.filter((emp) =>
    isAssignedToPending(emp.employee_id),
  ).length;

  const totalEmployees = employees.length;

  const assignedCount = useMemo(
    () =>
      employees.filter((emp) => (assignments[emp.employee_id] || []).length > 0)
        .length,
    [employees, assignments],
  );

  const unassignedCount = totalEmployees - assignedCount;

  // ============================================================
  // UNIVERSITY COUNTS
  // ============================================================

  const universityCounts = useMemo(() => {
    const counts = {};

    universities.forEach((university) => {
      counts[university.value] = employees.filter((emp) =>
        (assignments[emp.employee_id] || []).some(
          (assignedUniversity) =>
            String(assignedUniversity) === String(university.value),
        ),
      ).length;
    });

    return counts;
  }, [employees, assignments, universities]);

  // ============================================================
  // FILTER EMPLOYEES
  // ============================================================

  const filteredEmployees = useMemo(() => {
    const term = searchTerm.toLowerCase();

    return employees.filter((emp) => {
      const matchesSearch =
        emp.full_name?.toLowerCase().includes(term) ||
        emp.mobile_number?.includes(searchTerm);

      const current = assignments[emp.employee_id] || [];

      const matchesGroup =
        selectedGroup === "all" ||
        (selectedGroup === "unassigned" && current.length === 0) ||
        current.some((value) => String(value) === String(selectedGroup));

      return matchesSearch && matchesGroup;
    });
  }, [employees, searchTerm, selectedGroup, assignments]);

  // ============================================================
  // EMPLOYEE CHECKBOX
  // ============================================================

  const toggleEmployee = (employeeId) => {
    if (isAssignedToPending(employeeId)) return; // locked
    setSelectedEmployees((prev) =>
      prev.includes(employeeId)
        ? prev.filter((id) => id !== employeeId)
        : [...prev, employeeId],
    );
  };

  const selectableVisible = filteredEmployees.filter(
    (emp) => !isAssignedToPending(emp.employee_id),
  );

  const allVisibleSelected =
    selectableVisible.length > 0 &&
    selectableVisible.every((emp) =>
      selectedEmployees.includes(emp.employee_id),
    );

  const toggleSelectAllEmployees = () => {
    const visibleIds = selectableVisible.map((emp) => emp.employee_id);
    if (allVisibleSelected) {
      setSelectedEmployees((prev) =>
        prev.filter((id) => !visibleIds.includes(id)),
      );
    } else {
      setSelectedEmployees((prev) =>
        Array.from(new Set([...prev, ...visibleIds])),
      );
    }
  };

  // ============================================================
  // EMPLOYEE SELECT ALL
  // ============================================================

  // ============================================================
  // UNIVERSITY SELECTION (single select - tab style)
  // ============================================================

  const selectUniversity = (universityValue) => {
    setPendingUniversity(universityValue);
  };

  // ============================================================
  // CLEAR
  // ============================================================

  const clearSelection = () => {
    setSelectedEmployees([]);
    setPendingUniversity(null);
  };

  // ============================================================
  // ASSIGN
  // ============================================================

  const handleAssign = async () => {
    if (newSelectedEmployees.length === 0) {
      toast.error("Select at least one employee");
      return;
    }

    if (!pendingUniversity) {
      toast.error("Select a university");
      return;
    }

    setAssigning(true);

    try {
      const response = await fetch(SummaryApi.assignEmpToUniversity.url, {
        method: SummaryApi.assignEmpToUniversity.method,
        headers,
        body: JSON.stringify({
          employee_ids: newSelectedEmployees,
          university_ids: [pendingUniversity],
          assigned_by: getSessionEmployeeId(),
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.status) {
        toast.error(result.message || "Failed to assign university");
        return;
      }

      // Reflect the saved assignment in the UI
      setAssignments((prev) => {
        const next = { ...prev };

        newSelectedEmployees.forEach((employeeId) => {
          const current = next[employeeId] || [];
          next[employeeId] = Array.from(
            new Set([...current, pendingUniversity]),
          );
        });

        return next;
      });

      const universityLabel =
        universities.find(
          (item) => String(item.value) === String(pendingUniversity),
        )?.label || "University";

      const newlySaved =
        (result.assigned_count || 0) + (result.reactivated_count || 0);

      if (newlySaved === 0) {
        toast.info(result.message || "Already assigned");
      } else {
        toast.success(
          `Assigned ${universityLabel} to ${newlySaved} ${newlySaved > 1 ? "employees" : "employee"
          }`,
        );
      }

      clearSelection();
    } catch (error) {
      console.error("Assign University Error:", error);
      toast.error("Error assigning university");
    } finally {
      setAssigning(false);
    }
  };
  // ============================================================
  // FETCH SAVED ASSIGNMENTS
  // employee_id -> [university ids]
  // ============================================================

  const fetchAssignments = async () => {
    try {
      const response = await fetch(SummaryApi.getAllEmpUniversity.url, {
        method: SummaryApi.getAllEmpUniversity.method,
        headers,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch assignments");
      }

      const result = await response.json();

      if (result?.status && Array.isArray(result.data)) {
        const map = {};

        result.data.forEach((row) => {
          const employeeId = String(row.employee_id);
          map[employeeId] = [
            ...(map[employeeId] || []),
            String(row.university_id),
          ];
        });

        setAssignments(map);
      } else {
        setAssignments({});
      }
    } catch (error) {
      console.error("Assignments API Error:", error);
      toast.error(`Error fetching assignments: ${error.message}`);
    }
  };

  useEffect(() => {
    fetchAssignments();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ============================================================
  // REMOVE UNIVERSITY
  // ============================================================

  const handleRemove = async (employeeId, universityValue) => {
    try {
      const response = await fetch(SummaryApi.unassignEmpFromUniversity.url, {
        method: SummaryApi.unassignEmpFromUniversity.method,
        headers,
        body: JSON.stringify({
          employee_ids: [employeeId],
          university_id: universityValue,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.status) {
        toast.error(result.message || "Failed to remove university");
        return;
      }

      setAssignments((prev) => ({
        ...prev,
        [employeeId]: (prev[employeeId] || []).filter(
          (value) => String(value) !== String(universityValue)
        ),
      }));

      const university = universities.find(
        (item) => String(item.value) === String(universityValue)
      );


    } catch (error) {
      console.error("Unassign University Error:", error);
      toast.error("Error removing university");
    }
  };

  // ============================================================
  // STAT BOXES
  // ============================================================

  const statBoxes = [
    {
      value: "all",
      label: "All Employees",
      count: totalEmployees,
      icon: FaUsers,
      iconBg: "bg-gray-100",
      iconColor: "text-gray-600",
    },

    ...universities.map((university) => ({
      value: university.value,
      label: university.label,
      count: universityCounts[university.value] || 0,
      icon: FaUniversity,
      iconBg: "bg-green-50",
      iconColor: "text-green-600",
    })),

    {
      value: "unassigned",
      label: "Unassigned",
      count: unassignedCount,
      icon: FaExclamationCircle,
      iconBg: "bg-red-50",
      iconColor: "text-red-500",
    },
  ];

  const activeLabel =
    statBoxes.find((item) => String(item.value) === String(selectedGroup))
      ?.label || "All Employees";
  const canAssign =
    newSelectedEmployees.length > 0 && pendingUniversity !== null;

  // ============================================================
  // UI
  // ============================================================

  return (
    <div className="p-3 sm:p-5 lg:p-6">
      {/* ======================================================
          HEADER
      ====================================================== */}

      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div
            className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl shadow-sm"
            style={{
              background: "linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%)",
            }}
          >
            <FaLayerGroup
              className="text-xl"
              style={{
                color: "#c1272d",
              }}
            />
          </div>

        </div>

        <div className="flex w-fit items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 shadow-sm">
          <FaUserGraduate
            className="text-sm"
            style={{
              color: "#c1272d",
            }}
          />

          <span className="text-sm font-semibold text-gray-700">
            {totalEmployees} Employees
          </span>
        </div>
      </div>

      {/* ======================================================
          STAT BOXES
      ====================================================== */}

      <div
        className={`mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 ${universities.length + 2 <= 5 ? "xl:grid-cols-5" : "xl:grid-cols-5"
          }`}
      >
        {statBoxes.map((box) => {
          const Icon = box.icon;

          const active = String(selectedGroup) === String(box.value);

          return (
            <button
              key={String(box.value)}
              type="button"
              onClick={() => setSelectedGroup(box.value)}
              className={`rounded-2xl border bg-white p-5 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${active
                ? "border-[#c1272d] ring-2 ring-red-100"
                : "border-gray-100"
                }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-500">
                    {box.label}
                  </p>

                  <h2 className="mt-2 text-2xl font-bold text-gray-800">
                    {box.count}
                  </h2>
                </div>

                <div
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${box.iconBg}`}
                >
                  <Icon className={`text-lg ${box.iconColor}`} />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* ======================================================
          MAIN CONTENT
      ====================================================== */}

      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        {/* ====================================================
            UNIVERSITY SIDE
        ==================================================== */}

        <aside className="w-full shrink-0 rounded-2xl border border-gray-100 bg-white shadow-sm lg:sticky lg:top-4 lg:w-80">
          <div className="border-b border-gray-100 px-5 py-4">
            <h2 className="text-sm font-bold text-gray-800">
              Assign Universities
            </h2>

            <p className="mt-0.5 text-xs text-gray-400">
              Choose universities for the selected employees
            </p>
          </div>

          <div className="space-y-4 p-5">
            {/* ================================================
                UNIVERSITY LIST
            ================================================= */}

            {universityLoading ? (
              <div className="flex items-center justify-center py-8">
                <div
                  className="h-7 w-7 animate-spin rounded-full border-4 border-gray-200"
                  style={{
                    borderTopColor: "#c1272d",
                  }}
                />
              </div>
            ) : universities.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-200 p-5 text-center">
                <FaUniversity className="mx-auto mb-2 text-2xl text-gray-300" />

                <p className="text-sm font-semibold text-gray-600">
                  No active universities
                </p>

                <p className="mt-1 text-xs text-gray-400">
                  No active universities were returned from the API.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {universities.map((university) => {
                  const selected =
                    String(pendingUniversity) === String(university.value);

                  return (
                    <button
                      key={String(university.value)}
                      type="button"
                      onClick={() => selectUniversity(university.value)}
                      className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm font-semibold transition-all ${selected
                        ? "border-red-300 bg-red-50 text-[#c1272d] shadow-sm"
                        : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
                        }`}
                    >
                      <FaUniversity
                        className={
                          selected ? "text-[#c1272d]" : "text-green-600"
                        }
                      />

                      <span className="flex-1">{university.label}</span>

                      {selected && (
                        <span className="rounded-full bg-red-100 px-2 py-1 text-[10px] font-bold text-[#c1272d]">
                          Selected
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* ================================================
                BUTTONS
            ================================================= */}

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleAssign}
                disabled={!canAssign || assigning}
                className="flex-1 rounded-xl px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-all disabled:cursor-not-allowed disabled:opacity-40"
                style={{
                  background: "linear-gradient(135deg, #e05260, #c1272d)",
                }}
              >
                {assigning ? "Assigning..." : "Assign"}
              </button>

              <button
                type="button"
                onClick={clearSelection}
                disabled={
                  assigning ||
                  (selectedEmployees.length === 0 && pendingUniversity === null)
                }
                className="rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Clear
              </button>
            </div>
          </div>
        </aside>

        {/* ====================================================
            EMPLOYEE SIDE
        ==================================================== */}

        <section className="min-w-0 flex-1 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          {/* ================================================
              TOOLBAR
          ================================================= */}

          <div className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-sm font-bold text-gray-800">{activeLabel}</h2>

              <p className="mt-0.5 text-xs text-gray-400">
                {filteredEmployees.length} employee
                {filteredEmployees.length === 1 ? "" : "s"}
              </p>
            </div>

            <div className="relative w-full sm:max-w-xs">
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-400" />

              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or mobile number"
                className="w-full rounded-xl border border-gray-300 bg-white py-2 pl-11 pr-4 text-sm text-gray-800 outline-none transition-all placeholder:text-gray-400 focus:border-red-400 focus:ring-4 focus:ring-red-50"
              />
            </div>
          </div>

          {/* ================================================
              EMPLOYEE SELECT ALL
          ================================================= */}

          <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50 px-5 py-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold text-gray-700">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={toggleSelectAllEmployees}
                disabled={selectableVisible.length === 0}
                className="h-4 w-4 rounded border-gray-300 text-[#c1272d] focus:ring-red-400"
              />
              Select all employees
            </label>

            <span className="flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-600 shadow-sm">
              {newSelectedEmployees.length} selected
              {pendingUniversity !== null && (
                <span className="text-green-600">
                  · {alreadyAssignedCount} already assigned
                </span>
              )}
            </span>
          </div>

          {/* ================================================
              EMPLOYEE LIST
          ================================================= */}

          {loading ? (
            <div className="flex items-center justify-center p-16">
              <div
                className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200"
                style={{
                  borderTopColor: "#c1272d",
                }}
              />
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-16 text-center">
              <FaUserGraduate className="mb-3 text-4xl text-gray-300" />

              <p className="text-sm font-semibold text-gray-600">
                No employees found
              </p>

              <p className="mt-1 text-xs text-gray-400">
                Try another university or adjust your search.
              </p>
            </div>
          ) : (
            <ul className="max-h-[60vh] divide-y divide-gray-100 overflow-y-auto overscroll-contain">
              {filteredEmployees.map((emp) => {
                const current = assignments[emp.employee_id] || [];

                const currentUniversities = universities.filter((university) =>
                  current.some(
                    (value) => String(value) === String(university.value),
                  ),
                );

                const alreadyAssigned = isAssignedToPending(emp.employee_id);
                const isSelected =
                  alreadyAssigned ||
                  selectedEmployees.includes(emp.employee_id);

                return (
                  <li
                    key={emp.employee_id}
                    onClick={() => toggleEmployee(emp.employee_id)}
                    className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 transition-colors ${alreadyAssigned
                      ? "cursor-default bg-green-50/60"
                      : isSelected
                        ? "cursor-pointer bg-red-50/60"
                        : "cursor-pointer hover:bg-gray-50/60"
                      }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      disabled={alreadyAssigned}
                      onChange={() => toggleEmployee(emp.employee_id)}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 shrink-0 rounded border-gray-300 text-[#c1272d] focus:ring-red-400 disabled:cursor-not-allowed disabled:opacity-70"
                    />

                    {/* Avatar */}

                    <div
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold text-white"
                      style={{
                        background: getAvatarGradient(
                          emp.full_name || String(emp.employee_id),
                        ),
                      }}
                    >
                      {getInitials(emp.full_name)}
                    </div>

                    {/* Name */}

                    <p className="w-40 shrink-0 truncate text-sm font-semibold text-gray-800">
                      {emp.full_name || "Unnamed Employee"}
                    </p>

                    {/* Member Type */}

                    <span className="shrink-0 whitespace-nowrap rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                      {emp.member_type || "Employee"}
                    </span>

                    {/* Mobile */}

                    <span className="shrink-0 text-xs text-gray-500">
                      {emp.mobile_number}
                    </span>

                    {/* Assigned Universities */}

                    <div className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
                      {currentUniversities.length > 0 ? (
                        currentUniversities.map((university) => (
                          <span
                            key={String(university.value)}
                            className="flex items-center gap-1 whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-[#c1272d]"
                          >
                            {university.label}

                            <FaTimes
                              className="h-2.5 w-2.5 cursor-pointer hover:text-red-700"
                              onClick={(e) => {
                                e.stopPropagation();

                                handleRemove(emp.employee_id, university.value);
                              }}
                            />
                          </span>
                        ))
                      ) : (
                        <span className="flex items-center gap-1 whitespace-nowrap rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-500">
                          <FaExclamationCircle className="h-3 w-3 shrink-0" />
                          Unassigned
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default AssignUniversity;
